/**
 * The shell's navigation (13-08), as data. The surfaces are the three essential pages; the
 * existing pages sit under "More". Each surface's roles match its route guard in App.tsx, so no
 * one is shown a tab that would bounce them:
 *   - Verify: Oversight and every VerifierRole holder (D-13-R-6, 13-11.1; superseding D-13-08-2
 *     for Verification Log rows). Each lens sees only the rows its role verifies.
 *   - Release: Oversight only.
 */

import type { AppRole } from '../types/auth';

export interface NavEntry {
  label: string;
  path: string;
  /** Shown only to holders of one of these roles (absent = every office role). */
  roles?: readonly AppRole[];
  /** 13.2-11: a page that starts something (Lodge): the bar shows it as an action, "+ Lodge". */
  action?: boolean;
}

export const OVERSIGHT_ONLY: readonly AppRole[] = ['Oversight'];

/**
 * Who reaches `/verify` (D-13-R-6): Oversight, plus the App Roles a Verification Log row's
 * `VerifierRole` can name. The page shows each of them only the rows they can verify.
 */
export const VERIFY_ROLES: readonly AppRole[] = ['Oversight', 'CareManager', 'Finance', 'HRWHS'];

export const SURFACES: readonly NavEntry[] = [
  { label: 'Today', path: '/' },
  { label: 'Verify', path: '/verify', roles: VERIFY_ROLES },
  { label: 'Release', path: '/release', roles: OVERSIGHT_ONLY },
];

/**
 * 13.2-11 (D-13.2-A-6, D-13.2-P-12): the most-used pages, promoted out of More into the bar, for every office role.
 * D-13.2-A-9 (Andy, 26 Oct): Lookup joins them, the P&P look-up he wants CMs using often (supersedes D-13.2-A-7's
 * "leave it at two"). The action (+ Lodge) stays last. Below 1200 px the bar folds them back into More (AppBar).
 */
export const PINNED_PAGES: readonly NavEntry[] = [
  { label: 'Registers', path: '/registers' },
  { label: 'Lookup', path: '/lookup' },
  { label: 'Lodge', path: '/lodge', action: true },
];

export const MORE_PAGES: readonly NavEntry[] = [
  { label: 'Overview', path: '/overview' },
  { label: 'My incidents', path: '/my-incidents' },
  { label: 'Closure trail', path: '/trail' },
  // 13-32: Internal Audits, Oversight only (its route guard in App.tsx matches, the D-13-08-2 pattern).
  { label: 'Audits', path: '/audits', roles: OVERSIGHT_ONLY },
  // 13.1-11: the Operator's Manual, open to every QMS role (no `roles`).
  { label: 'Help', path: '/help' },
];

function visible(entries: readonly NavEntry[], roles: readonly AppRole[]): NavEntry[] {
  return entries.filter((e) => !e.roles || e.roles.some((r) => roles.includes(r)));
}

export function navFor(roles: readonly AppRole[]): { primary: NavEntry[]; pinned: NavEntry[]; more: NavEntry[] } {
  return { primary: visible(SURFACES, roles), pinned: visible(PINNED_PAGES, roles), more: visible(MORE_PAGES, roles) };
}

const ROLE_LABEL: ReadonlyArray<[AppRole, string]> = [
  ['Oversight', 'Oversight'],
  ['CareManager', 'Care Manager'],
  ['HRWHS', 'HR & WHS'],
  ['Finance', 'Finance'],
];

/** The person's headline role for the app bar (Oversight wins for a multi-role login). */
export function roleLabel(roles: readonly AppRole[]): string {
  return ROLE_LABEL.find(([r]) => roles.includes(r))?.[1] ?? 'Harmony QMS';
}
